import { useMemo, useCallback } from "react";

import { getZcCollateral, getZcLedger, LoanRepayType } from "@bridgesplit/abf-react";
import { roundToDecimals } from "@bridgesplit/utils";

import { MakeRepaymentProps } from "./types";
import { createRepaymentParams, useRepay } from "./util";
import { useLoanMarketHoursAlertConfig } from "../../util";
import { useManageLeverageContext } from "./ManageLeverageContext";
import { useBalanceChecker } from "../../../common";
import RepaymentComponent from "./RepaymentComponent";

export default function PrincipalRepayment({ loanExpanded }: MakeRepaymentProps) {
    const { setForm, form } = useManageLeverageContext();
    const { isClosed: partialDisabledMarketHours } = useLoanMarketHoursAlertConfig(loanExpanded);
    const ledger = getZcLedger(loanExpanded);

    // Principal-specific data
    const totalDue = ledger?.ledgerDebt.total ?? 0;
    const inputAmount = form.repayWithPrincipalAmount ?? 0;
    const effectiveInputAmount = form.isFullRepay ? totalDue : Math.min(inputAmount, totalDue);

    // Balance checking for principal token
    const {
        balance,
        insufficientBalance,
        isLoading: balanceLoading
    } = useBalanceChecker({
        mint: loanExpanded?.principalMetadata.mint,
        amount: effectiveInputAmount,
        escrowNeeded: false,
        skip: false
    });

    const params = createRepaymentParams({
        principalPaymentUiAmount: effectiveInputAmount,
        collateralPaymentUiAmount: undefined,
        isFullRepay: form.isFullRepay,
        repayType: LoanRepayType.Principal,
        collateral: getZcCollateral(loanExpanded),
        loanExpanded,
        quote: undefined
    });

    function setMaxRepay() {
        const maxRepayAmount = Math.min(ledger?.ledgerDebt.total ?? 0, balance);
        const isMaxRepay = maxRepayAmount === ledger?.ledgerDebt.total;

        setForm({
            ...form,
            isFullRepay: isMaxRepay,
            repayWithPrincipalAmount: maxRepayAmount,
            repayWithCollateralAmount: undefined
        });
    }

    const { repay } = useRepay(params);

    const handleInputChange = useCallback(
        (amt: number | undefined) => {
            const isFullRepay = amt === totalDue;

            setForm((prev) => ({
                ...prev,
                repayWithPrincipalAmount: amt,
                isFullRepay
            }));
        },
        [totalDue, setForm]
    );

    const ctaError = useMemo(() => {
        if (balanceLoading) return "Loading";
        if (!balance) return "No available balance";
        if (!effectiveInputAmount) return "Enter an amount";

        if (!form.isFullRepay && inputAmount > roundToDecimals(totalDue, 2)) {
            return "Exceeds total due";
        }
        if (insufficientBalance) return "Insufficient balance";
        return null;
    }, [effectiveInputAmount, form.isFullRepay, inputAmount, totalDue, insufficientBalance, balanceLoading, balance]);

    const inputValue = form.isFullRepay ? totalDue : inputAmount;

    return (
        <RepaymentComponent
            inputValue={inputValue}
            inputMetadata={loanExpanded?.principalMetadata}
            onInputChange={handleInputChange}
            disabledInput={!balance}
            topLevelTotalPrincipalDue={totalDue}
            principalMetadata={loanExpanded?.principalMetadata}
            totalDueInBalanceDisplay={totalDue}
            repaymentAmount={effectiveInputAmount}
            loading={false}
            balance={balance}
            balanceMetadata={loanExpanded?.principalMetadata}
            balanceLoading={balanceLoading}
            balanceError={false}
            error={null}
            ctaError={ctaError}
            onRepay={repay}
            onSetMax={setMaxRepay}
            showMarketHours={!!partialDisabledMarketHours && !form.isFullRepay}
            loanExpanded={loanExpanded}
            repayType={LoanRepayType.Principal}
            repayLabel="Repay"
        />
    );
}
